import React from 'react';
import {
  MapPin, Trophy, Sparkles, TrendingUp, Calendar, Users,
  Target, BarChart3, Home, Clock, Medal, Activity,
  Smartphone, Zap, Star, Award, Brain, Bell, Shield,
  Share2, Flame, ChevronUp, Search, QrCode, Hash
} from 'lucide-react';
import { cn } from '../lib/utils';
import { AIOrb } from './AIOrb';
import { Marquee } from './magicui/marquee';

interface AppFeaturesMarqueeProps {
  lang?: 'en' | 'de' | 'fr' | 'it';
}

const AppFeaturesMarquee: React.FC<AppFeaturesMarqueeProps> = ({ lang = 'en' }) => {
  const translations = {
    en: {
      features: [
        { icon: Search, name: 'Real-time Player Search', description: 'Instant search with autocomplete for 30,000+ players', isAI: false },
        { icon: QrCode, name: 'Profile Sharing QR Codes', description: 'Share player profiles with QR codes & links', isAI: false },
        { icon: Trophy, name: 'Live Match Tracking', description: 'Score tracking with real-time projected ELO', isAI: false },
        { icon: Target, name: 'Multi-Match ELO Calculator', description: 'Plan your tournament day ELO changes', isAI: false },
        { icon: Brain, name: 'AI Playing Style Analysis', description: 'Discover your unique playing style & patterns', isAI: true },
        { icon: Flame, name: 'Streak & Form Analysis', description: 'Track winning/losing runs & current form score', isAI: false },
        { icon: Award, name: '50+ Achievement System', description: 'Bronze, Silver, Gold & Special tiers to unlock', isAI: false },
        { icon: Bell, name: 'Match Push Notifications', description: 'Never miss a match with smart reminders', isAI: false },
        { icon: Calendar, name: 'Upcoming Matches Calendar', description: 'Complete schedule with league context', isAI: false },
        { icon: BarChart3, name: 'Performance Heatmaps', description: 'Monthly/yearly win rate visualizations', isAI: false },
        { icon: Users, name: 'Head-to-Head Analysis', description: 'Complete historical matchup records', isAI: false },
        { icon: TrendingUp, name: 'Classification Progression', description: 'Track your journey through A22-D1 tiers', isAI: false },
        { icon: Trophy, name: 'Tournament Finder', description: 'Browse & register for tournaments nearby', isAI: false },
        { icon: Medal, name: 'Games Score Distribution', description: '3-0, 3-1, 3-2 win/loss breakdown analysis', isAI: false },
        { icon: MapPin, name: 'League Tables & Standings', description: 'Live standings with team navigation', isAI: false },
        { icon: Activity, name: 'Win Probability Engine', description: 'Calculate match outcomes with ELO & statistics', isAI: true },
        { icon: ChevronUp, name: 'Win Rate by Rating Levels', description: 'Performance vs higher/lower/equal rated opponents', isAI: false },
        { icon: Hash, name: 'Swiss National Rankings', description: 'Official monthly STT rankings with filters', isAI: false },
        { icon: Home, name: 'Competition Heatmaps', description: 'Performance analysis by league/tournament', isAI: false },
        { icon: Shield, name: 'Mental Fortitude Score', description: 'AI-powered psychological game assessment', isAI: true },
        { icon: Share2, name: 'Advanced Statistics', description: 'Volatility, consistency & trend analysis', isAI: false },
        { icon: Star, name: 'Unlimited Favorites', description: 'Follow players with instant profile access', isAI: false },
        { icon: Clock, name: 'Smart Search History', description: 'Recently viewed players & quick access', isAI: false },
        { icon: Sparkles, name: 'Material 3 Design', description: 'Dark/light theme with smooth animations', isAI: false },
        { icon: Zap, name: 'Offline-First Design', description: 'Access cached data without internet', isAI: false },
        { icon: Smartphone, name: 'Kotlin Multiplatform', description: 'Native iOS & Android with Compose UI', isAI: false },
      ]
    },
    de: {
      features: [
        { icon: Search, name: 'Echtzeit-Spielersuche', description: 'Sofortsuche mit Autocomplete für 30.000+ Spieler', isAI: false },
        { icon: QrCode, name: 'Profil-QR-Codes teilen', description: 'Spielerprofile mit QR-Codes & Links teilen', isAI: false },
        { icon: Trophy, name: 'Live-Match-Tracking', description: 'Punktestand mit Echtzeit-ELO-Projektion', isAI: false },
        { icon: Target, name: 'Multi-Match-ELO-Rechner', description: 'Plane deine Turniertag-ELO-Änderungen', isAI: false },
        { icon: Brain, name: 'KI-Spielstil-Analyse', description: 'Entdecke deinen einzigartigen Spielstil & Muster', isAI: true },
        { icon: Flame, name: 'Serien & Form-Analyse', description: 'Verfolge Sieges-/Verlustserien & aktuelle Form', isAI: false },
        { icon: Award, name: '50+ Achievement-System', description: 'Bronze, Silber, Gold & Spezial-Stufen freischalten', isAI: false },
        { icon: Bell, name: 'Match-Push-Benachrichtigungen', description: 'Verpasse kein Spiel mit smarten Erinnerungen', isAI: false },
        { icon: Calendar, name: 'Kommende Spiele Kalender', description: 'Kompletter Spielplan mit Liga-Kontext', isAI: false },
        { icon: BarChart3, name: 'Leistungs-Heatmaps', description: 'Monatliche/jährliche Siegquoten-Visualisierung', isAI: false },
        { icon: Users, name: 'Direktvergleich-Analyse', description: 'Komplette historische Duell-Aufzeichnungen', isAI: false },
        { icon: TrendingUp, name: 'Klassifizierungs-Progression', description: 'Verfolge deine Reise durch A22-D1 Stufen', isAI: false },
        { icon: Trophy, name: 'Turnier-Finder', description: 'Durchsuche & melde dich für Turniere an', isAI: false },
        { icon: Medal, name: 'Spielverteilung Punktestand', description: '3-0, 3-1, 3-2 Sieg/Niederlage-Analyse', isAI: false },
        { icon: MapPin, name: 'Ligatabellen & Stände', description: 'Live-Stände mit Team-Navigation', isAI: false },
        { icon: Activity, name: 'Gewinnwahrscheinlichkeits-Engine', description: 'Berechne Match-Ergebnisse mit ELO & Statistiken', isAI: true },
        { icon: ChevronUp, name: 'Siegquote nach Rating-Level', description: 'Leistung vs höher/niedriger/gleich bewertete Gegner', isAI: false },
        { icon: Hash, name: 'Schweizer National-Rangliste', description: 'Offizielle monatliche STT-Rankings mit Filtern', isAI: false },
        { icon: Home, name: 'Wettbewerbs-Heatmaps', description: 'Leistungsanalyse nach Liga/Turnier', isAI: false },
        { icon: Shield, name: 'Mentale Stärke-Bewertung', description: 'KI-gestützte psychologische Spiel-Bewertung', isAI: true },
        { icon: Share2, name: 'Erweiterte Statistiken', description: 'Volatilität, Konsistenz & Trend-Analyse', isAI: false },
        { icon: Star, name: 'Unbegrenzte Favoriten', description: 'Folge Spielern mit sofortigem Profil-Zugriff', isAI: false },
        { icon: Clock, name: 'Smarter Suchverlauf', description: 'Kürzlich angesehene Spieler & Schnellzugriff', isAI: false },
        { icon: Sparkles, name: 'Material 3 Design', description: 'Dark/Light Theme mit sanften Animationen', isAI: false },
        { icon: Zap, name: 'Offline-First Design', description: 'Zugriff auf zwischengespeicherte Daten ohne Internet', isAI: false },
        { icon: Smartphone, name: 'Kotlin Multiplatform', description: 'Native iOS & Android mit Compose UI', isAI: false },
      ]
    },
    fr: {
      features: [
        { icon: Search, name: 'Recherche Joueur Temps Réel', description: 'Recherche instantanée avec autocomplétion pour 30.000+ joueurs', isAI: false },
        { icon: QrCode, name: 'QR Codes de Partage Profil', description: 'Partage profils joueurs avec QR codes & liens', isAI: false },
        { icon: Trophy, name: 'Suivi Match en Direct', description: 'Score avec projection ELO temps réel', isAI: false },
        { icon: Target, name: 'Calculateur ELO Multi-Matchs', description: 'Planifie tes changements ELO de tournoi', isAI: false },
        { icon: Brain, name: 'Analyse IA Style de Jeu', description: 'Découvre ton style de jeu unique & patterns', isAI: true },
        { icon: Flame, name: 'Analyse Séries & Forme', description: 'Suivi séries gagnantes/perdantes & score forme', isAI: false },
        { icon: Award, name: 'Système 50+ Succès', description: 'Bronze, Argent, Or & niveaux Spéciaux à débloquer', isAI: false },
        { icon: Bell, name: 'Notifications Push Matchs', description: 'Ne rate aucun match avec rappels intelligents', isAI: false },
        { icon: Calendar, name: 'Calendrier Matchs à Venir', description: 'Programme complet avec contexte ligue', isAI: false },
        { icon: BarChart3, name: 'Cartes Thermiques Performance', description: 'Visualisations taux victoire mensuel/annuel', isAI: false },
        { icon: Users, name: 'Analyse Face-à-Face', description: 'Enregistrements historiques matchs complets', isAI: false },
        { icon: TrendingUp, name: 'Progression Classification', description: 'Suis ton parcours à travers niveaux A22-D1', isAI: false },
        { icon: Trophy, name: 'Chercheur Tournois', description: 'Parcours & inscris-toi aux tournois proches', isAI: false },
        { icon: Medal, name: 'Distribution Score Jeux', description: 'Analyse victoires/défaites 3-0, 3-1, 3-2', isAI: false },
        { icon: MapPin, name: 'Tableaux & Classements Ligue', description: 'Classements live avec navigation équipe', isAI: false },
        { icon: Activity, name: 'Moteur Probabilité Victoire', description: 'Calcule résultats matchs avec ELO & stats', isAI: true },
        { icon: ChevronUp, name: 'Taux Victoire par Niveaux', description: 'Performance vs adversaires +fort/faible/égal', isAI: false },
        { icon: Hash, name: 'Classement National Suisse', description: 'Classements STT mensuels officiels avec filtres', isAI: false },
        { icon: Home, name: 'Cartes Thermiques Compétition', description: 'Analyse performance par ligue/tournoi', isAI: false },
        { icon: Shield, name: 'Score Fortitude Mentale', description: 'Évaluation psychologique jeu par IA', isAI: true },
        { icon: Share2, name: 'Statistiques Avancées', description: 'Analyse volatilité, cohérence & tendances', isAI: false },
        { icon: Star, name: 'Favoris Illimités', description: 'Suis joueurs avec accès profil instantané', isAI: false },
        { icon: Clock, name: 'Historique Recherche Intelligent', description: 'Joueurs récents & accès rapide', isAI: false },
        { icon: Sparkles, name: 'Design Material 3', description: 'Thème sombre/clair avec animations fluides', isAI: false },
        { icon: Zap, name: 'Design Offline-First', description: 'Accès données en cache sans internet', isAI: false },
        { icon: Smartphone, name: 'Kotlin Multiplateforme', description: 'iOS & Android natifs avec Compose UI', isAI: false },
      ]
    },
    it: {
      features: [
        { icon: Search, name: 'Ricerca Giocatore Tempo Reale', description: 'Ricerca istantanea con autocompletamento per 30.000+ giocatori', isAI: false },
        { icon: QrCode, name: 'QR Code Condivisione Profilo', description: 'Condividi profili giocatori con QR code & link', isAI: false },
        { icon: Trophy, name: 'Tracciamento Match Live', description: 'Punteggio con proiezione ELO tempo reale', isAI: false },
        { icon: Target, name: 'Calcolatore ELO Multi-Match', description: 'Pianifica i tuoi cambiamenti ELO torneo', isAI: false },
        { icon: Brain, name: 'Analisi AI Stile Gioco', description: 'Scopri tuo stile unico & pattern di gioco', isAI: true },
        { icon: Flame, name: 'Analisi Streak & Forma', description: 'Traccia serie vincenti/perdenti & punteggio forma', isAI: false },
        { icon: Award, name: 'Sistema 50+ Achievement', description: 'Bronzo, Argento, Oro & livelli Speciali da sbloccare', isAI: false },
        { icon: Bell, name: 'Notifiche Push Partite', description: 'Non perdere partite con promemoria intelligenti', isAI: false },
        { icon: Calendar, name: 'Calendario Partite Imminenti', description: 'Programma completo con contesto lega', isAI: false },
        { icon: BarChart3, name: 'Mappe Termiche Prestazioni', description: 'Visualizzazioni tasso vittoria mensile/annuale', isAI: false },
        { icon: Users, name: 'Analisi Testa-a-Testa', description: 'Registri storici completi sfide', isAI: false },
        { icon: TrendingUp, name: 'Progressione Classificazione', description: 'Segui il tuo percorso attraverso livelli A22-D1', isAI: false },
        { icon: Trophy, name: 'Trova Tornei', description: 'Sfoglia & iscriviti a tornei vicini', isAI: false },
        { icon: Medal, name: 'Distribuzione Punteggio Giochi', description: 'Analisi vittorie/sconfitte 3-0, 3-1, 3-2', isAI: false },
        { icon: MapPin, name: 'Classifiche & Tabelle Lega', description: 'Classifiche live con navigazione squadra', isAI: false },
        { icon: Activity, name: 'Motore Probabilità Vittoria', description: 'Calcola risultati partite con ELO & statistiche', isAI: true },
        { icon: ChevronUp, name: 'Tasso Vittoria per Livelli', description: 'Prestazioni vs avversari +forte/debole/uguale', isAI: false },
        { icon: Hash, name: 'Classifica Nazionale Svizzera', description: 'Classifiche STT mensili ufficiali con filtri', isAI: false },
        { icon: Home, name: 'Mappe Termiche Competizione', description: 'Analisi prestazioni per lega/torneo', isAI: false },
        { icon: Shield, name: 'Punteggio Fortezza Mentale', description: 'Valutazione psicologica gioco con AI', isAI: true },
        { icon: Share2, name: 'Statistiche Avanzate', description: 'Analisi volatilità, coerenza & tendenze', isAI: false },
        { icon: Star, name: 'Preferiti Illimitati', description: 'Segui giocatori con accesso profilo istantaneo', isAI: false },
        { icon: Clock, name: 'Storico Ricerche Intelligente', description: 'Giocatori recenti & accesso rapido', isAI: false },
        { icon: Sparkles, name: 'Design Material 3', description: 'Tema scuro/chiaro con animazioni fluide', isAI: false },
        { icon: Zap, name: 'Design Offline-First', description: 'Accesso dati in cache senza internet', isAI: false },
        { icon: Smartphone, name: 'Kotlin Multipiattaforma', description: 'iOS & Android nativi con Compose UI', isAI: false },
      ]
    }
  };

  const t = translations[lang];
  const features = t.features;

  const aiText = {
    en: 'AI',
    de: 'KI',
    fr: 'IA',
    it: 'IA'
  }[lang];

  // Split features into 3 rows
  const rowSize = Math.ceil(features.length / 3);
  const firstRow = features.slice(0, rowSize);
  const secondRow = features.slice(rowSize, rowSize * 2);
  const thirdRow = features.slice(rowSize * 2);

  const FeatureCard = ({
    icon: Icon,
    name,
    description,
    isAI = false,
  }: {
    icon: any;
    name: string;
    description: string;
    isAI?: boolean;
  }) => {
    return (
      <figure
        className={cn(
          "relative w-72 h-24 overflow-hidden rounded-xl p-4",
          "bg-white dark:bg-gray-800 shadow-sm transition-colors duration-300"
        )}
      >
        <div className="flex flex-row items-center gap-3 h-full">
          {isAI ? (
            <div className="flex-shrink-0">
              <AIOrb size={36} showText={true} text={aiText} />
            </div>
          ) : (
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center transition-colors duration-300">
              <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400 transition-colors duration-300" />
            </div>
          )}
          <div className="flex flex-col flex-1 min-w-0 justify-center">
            <figcaption className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight mb-1 transition-colors duration-300">
              {name}
            </figcaption>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight line-clamp-2 transition-colors duration-300">{description}</p>
          </div>
        </div>
      </figure>
    );
  };

  return (
    <div className="relative flex w-full flex-col items-center justify-center overflow-hidden rounded-xl py-6">
      <Marquee pauseOnHover className="mb-3">
        {firstRow.map((feature, idx) => (
          <div key={idx} className="mr-4">
            <FeatureCard {...feature} />
          </div>
        ))}
      </Marquee>
      <Marquee reverse pauseOnHover className="mb-3">
        {secondRow.map((feature, idx) => (
          <div key={idx} className="mr-4">
            <FeatureCard {...feature} />
          </div>
        ))}
      </Marquee>
      <Marquee pauseOnHover>
        {thirdRow.map((feature, idx) => (
          <div key={idx} className="mr-4">
            <FeatureCard {...feature} />
          </div>
        ))}
      </Marquee>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-gray-50 dark:from-gray-900"></div>
      <div className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-gray-50 dark:from-gray-900"></div>
    </div>
  );
};

export default AppFeaturesMarquee;
